"use client";
import { gsap } from "gsap";
import { Flip } from "gsap/Flip";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollSmoother } from "gsap/ScrollSmoother";
import { SplitText } from "gsap/SplitText";
import { DrawSVGPlugin } from "gsap/DrawSVGPlugin";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import MotionPathPlugin from "gsap/MotionPathPlugin";
import { useGSAP } from "@gsap/react";
import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { smoothScrollToElement } from "../utils/scrollUtils";

gsap.registerPlugin(
  ScrollTrigger,
  ScrollSmoother,
  SplitText,
  Flip,
  DrawSVGPlugin,
  ScrollToPlugin,
  MotionPathPlugin
);

export const Animated = ({ children }: { children: React.ReactNode }) => {
  const pathName = usePathname();

  useEffect(() => {
    // Check if there's a hash in the URL
    const hash = window.location.hash.slice(1); // Remove the '#'

    if (hash) {
      // If there's a hash, scroll to it after everything is ready
      const scrollToHash = () => {
        smoothScrollToElement(hash);

        // Remove hash from URL after scrolling
        setTimeout(() => {
          window.history.replaceState(null, "", window.location.pathname);
        }, 100);
      };

      // Wait for DOM to be fully ready
      if (document.readyState === "complete") {
        setTimeout(scrollToHash, 200);
      } else {
        window.addEventListener("load", () => {
          setTimeout(scrollToHash, 200);
        });
      }

      return; // Don't force scroll to top
    }

    // Only force scroll to top if there's no hash (normal page load)
    window.scrollTo(0, 0);
  }, [pathName]);

  useGSAP(() => {
    // Detect mobile devices
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    // Configure ScrollTrigger for better mobile performance
    ScrollTrigger.config({
      // Reduce precision on mobile to improve performance
      autoRefreshEvents: isMobile
        ? "visibilitychange,DOMContentLoaded,load"
        : "visibilitychange,DOMContentLoaded,load,resize",
      ignoreMobileResize: true, // Prevent resize events from triggering on mobile scroll
    });

    // Only create ScrollSmoother on non-mobile devices.
    let smoother: ScrollSmoother | undefined = undefined;
    if (!isMobile) {
      window.ScrollSmoother = ScrollSmoother;
      smoother = ScrollSmoother.create({
        smooth: 1,
        smoothTouch: false,
        effects: true,
        wrapper: "#smooth-wrapper",
        content: "#smooth-content",
        normalizeScroll: isMobile,
      });
    } else {
      // Ensure no global smoother is left on mobile
      window.ScrollSmoother = undefined;
    }

    // Refresh to ensure proper initialization
    ScrollTrigger.refresh();

    return () => {
      smoother?.kill?.();
    };
  });

  return <>{children}</>;
};
